/* pb-cart.js — Headless unified cart via Storefront API */

// ---------- Consent & Klaviyo helpers ----------
function canUserBeTracked() {
  try {
    return !!(window.Shopify && Shopify.customerPrivacy && Shopify.customerPrivacy.userCanBeTracked());
  } catch (e) { return false; }
}

// Wait for consent if not granted yet, with a timeout fallback
function waitForConsent(ms = 5000) {
  return new Promise((resolve) => {
    if (canUserBeTracked()) return resolve(true);

    let done = false;
    const onConsent = () => { if (!done) { done = true; resolve(true); } };

    // Shopify emits this after the banner choice (name can vary by app; this one is most common)
    document.addEventListener('visitorConsentCollected', onConsent, { once: true });

    setTimeout(() => { if (!done) resolve(false); }, ms);
  });
}

// Ensure Klaviyo queue exists and retry a few times for library readiness
function klaviyoQueueReady() {
  return !!(window._learnq && window._learnq._loaded === true && typeof window._learnq.push === 'function');
}


async function ensureKlaviyoReady(retries = 25, waitMs = 200) {
  // don't create _learnq here — let Klaviyo own it
  for (let i = 0; i < retries; i++) {
    if (klaviyoQueueReady()) return true;
    await new Promise(r => setTimeout(r, waitMs));
  }

  console.warn('[Klaviyo] _learnq not loaded — event queued only');
  // fallback: create queue so we don't crash
  window._learnq = window._learnq || [];
  return false;
}

// Identify helper: prefer quiz email, then Shopify customer if theme exposes it
function klaviyoIdentifyIfPossible() {
  try {
    window._learnq = window._learnq || [];

    const quizEmail = localStorage.getItem('quiz_email');
    const themeCustomerEmail =
      window?.Shopify?.customer?.email || window?.__PB_CUSTOMER_EMAIL__ || '';

    const $email = (quizEmail && quizEmail.trim()) ? quizEmail.trim()
                  : (themeCustomerEmail && String(themeCustomerEmail).trim())
                  ? String(themeCustomerEmail).trim()
                  : '';

    if ($email) {
      window._learnq.push(['identify', { $email }]);
      window.PBDebug?.log('[Klaviyo] identify', { $email });
      return true;
    }
  } catch (e) {
    console.warn('[Klaviyo] identify failed', e);
  }
  return false;
}

// Unified safe tracker that waits for consent + (best-effort) Klaviyo readiness
async function klaviyoTrack(eventName, props = {}) {
  try {
    // Wait for consent (flushes later if user accepts)
    const consentOk = canUserBeTracked() ? true : await waitForConsent(6000);
    if (!consentOk && !canUserBeTracked()) {
      console.warn('[Klaviyo] consent not granted, skipping event:', eventName);
      return false;
    }

    await ensureKlaviyoReady();
    klaviyoIdentifyIfPossible();
    
    window._learnq = window._learnq || [];
    window._learnq.push(['track', eventName, props]);
    window.PBDebug?.log('[Klaviyo] track →', eventName, props);
    return true;
  } catch (e) {
    console.warn('[Klaviyo] track failed', e);
    return false;
  }
}


(() => {
  // ---------- Config ----------
  const cfg = () => (window.PB_UNIFIED_CART || {});
  const ENDPOINT = () => cfg().endpoint;
  const TOKEN    = () => cfg().token;
  const COOKIE_DOMAIN = () => (cfg().cookieDomain || apex(location.hostname));
  const AUTO_HOOK = () => cfg().autoHookAddToCart === true;

  // Wait for endpoint + token
  const waitForConfig = async () => {
    let tries = 0;
    while (!ENDPOINT() || !TOKEN()) {
      await new Promise(r => setTimeout(r, 30));
      if (++tries > 200) throw new Error('[PBCart] Missing endpoint/token config');
    }
  };

  // Apex helper
  function apex(host) {
    const parts = String(host || location.hostname).split('.');
    return parts.length >= 2 ? `.${parts.slice(-2).join('.')}` : `.${host}`;
  }

  // ---------- Cookie ----------
  const CART_COOKIE = 'pb_cart_id';

  function setCookie(name, val, days = 30) {
    const d = new Date();
    d.setTime(d.getTime() + days * 864e5);
    document.cookie = `${name}=${encodeURIComponent(val)};expires=${d.toUTCString()};path=/;domain=${COOKIE_DOMAIN()};SameSite=None;Secure`;
  }

  function getCookie(name) {
    const hit = document.cookie.split('; ').find(r => r.startsWith(name + '='));
    return hit ? decodeURIComponent(hit.split('=')[1]) : null;
  }

  function delCookie(name) {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/;domain=${COOKIE_DOMAIN()};SameSite=None;Secure`;
  }

  // ---------- GQL (with RETRY + THROTTLE handling) ----------
  async function gql(query, variables = {}) {
    await waitForConfig();

    let attempts = 0;

    while (attempts < 5) {
      const res = await fetch(ENDPOINT(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": TOKEN()
        },
        body: JSON.stringify({ query, variables })
      });

      const json = await res.json();

      // SUCCESS
      if (json.data && !json.errors) {
        return json.data;
      }

      // THROTTLED → retry
      if (json.errors && json.errors[0]?.extensions?.code === "THROTTLED") {
        const waitMs = 300 * (attempts + 1);
        console.warn(`[PBCart][THROTTLED] waiting ${waitMs}ms…`);
        await new Promise(r => setTimeout(r, waitMs));
        attempts++;
        continue;
      }

      // OTHER ERRORS
      throw new Error(`[PBCart] GQL error: ${JSON.stringify(json.errors)}`);
    }

    throw new Error("[PBCart] Failed after max retries (THROTTLED)");
  }

  // ---------- Cart Fragment ----------
  const CART_FRAGMENT = `
  fragment CartMini on Cart {
    id
    checkoutUrl
    totalQuantity
    attributes { key value }

    cost {
      subtotalAmount { amount currencyCode }
      totalAmount { amount currencyCode }
    }

    discountCodes {
      applicable
      code
    }
    
    lines(first: 100) {
      edges {
        node {
          id
          quantity
          attributes { key value }

          discountAllocations {
            discountedAmount { amount currencyCode }
          }

          cost {
            subtotalAmount { amount currencyCode }
            totalAmount { amount currencyCode }
          }

          merchandise {
            __typename
            ... on ProductVariant {
              id
              title
              sku
              image { url altText }
              price { amount currencyCode }
              compareAtPrice { amount currencyCode }
              product {
                id title handle vendor
                cardTitle: metafield(namespace: "custom", key: "product_card_title") { value }
                subtitle: metafield(namespace: "custom", key: "product_subtitle") { value }
                ratingFor: metafield(namespace: "custom", key: "collection_page_rating_for") { value }
                cartNaming: metafield(namespace: "custom", key: "cart_naming") { value }
              }

              requiresComponents
              components(first: 30) {
                nodes {
                  quantity
                  productVariant {
                    id
                    title
                    sku
                    image { url altText }
                    product { id title handle vendor }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;


  // ---------- API OPS ----------
  async function cartCreate() {
    const d = await gql(`mutation { cartCreate(input:{}) { cart { ...CartMini } } } ${CART_FRAGMENT}`);
    return d.cartCreate.cart;
  }

  async function cartQuery(id) {
    const d = await gql(`query($id:ID!){ cart(id:$id){ ...CartMini } } ${CART_FRAGMENT}`, { id });
    return d.cart;
  }

  async function cartLinesAdd(cartId, lines) {
    const d = await gql(`
      mutation($cartId:ID!, $lines:[CartLineInput!]!) {
        cartLinesAdd(cartId:$cartId, lines:$lines) { cart { ...CartMini } }
      }
      ${CART_FRAGMENT}
    `, { cartId, lines });
    return d.cartLinesAdd.cart;
  }

  async function cartLinesUpdate(cartId, lines) {    
    const d = await gql(`
      mutation($cartId:ID!,$lines:[CartLineUpdateInput!]!) {
        cartLinesUpdate(cartId:$cartId, lines:$lines) { cart { ...CartMini } }
      }
      ${CART_FRAGMENT}
    `, { cartId, lines });
    return d.cartLinesUpdate.cart;
  }

  async function cartLinesRemove(cartId, lineIds) {
    const d = await gql(`
      mutation($cartId:ID!,$lineIds:[ID!]!) {
        cartLinesRemove(cartId:$cartId, lineIds:$lineIds) { cart { ...CartMini } }
      }
      ${CART_FRAGMENT}
    `, { cartId, lineIds });
    return d.cartLinesRemove.cart;
  }

  async function cartAttributesUpdate(cartId, attributes) {
    const d = await gql(`
      mutation($cartId:ID!,$attributes:[AttributeInput!]!) {
        cartAttributesUpdate(cartId:$cartId, attributes:$attributes) { cart { ...CartMini } }
      }
      ${CART_FRAGMENT}
    `, { cartId, attributes });
    return d.cartAttributesUpdate.cart;
  }

  // ---------- Boot ----------
  let _cartId = null;
  let _cartPromise = null;

  async function ensureCart() {
    await waitForConfig();

    if (_cartId) return _cartId;

    // try cookie
    const cookieId = getCookie(CART_COOKIE);
    if (cookieId) {
      try {
        const c = await cartQuery(cookieId);
        if (c?.id) {
          _cartId = c.id;
          _cartPromise = Promise.resolve(c);
          return _cartId;
        }
      } catch (err) {}
    }

    // create new cart
    let created = await cartCreate();

    // ensure first-touch attribute is set on cart
    created = await ensureAttributionAttributes(created);

    _cartId = created.id;
    _cartPromise = Promise.resolve(created);
    setCookie(CART_COOKIE, _cartId);

    document.dispatchEvent(updatedEvent(created, "boot"));
    return _cartId;
  }

  function gidFromVariant(id) {
    const s = String(id);
    return s.startsWith("gid://")
      ? s
      : `gid://shopify/ProductVariant/${s.replace(/\D/g, "")}`;
  }

  function updatedEvent(cart, source = "update") {
    return new CustomEvent("pb:cart:updated", {
      detail: { source, cart }
    });
  }

  // ---------- First-touch + Last-touch brand → Cart attributes ----------
  const FIRST_TOUCH_COOKIE = 'pb_first_touch_brand';
  const FIRST_TOUCH_KEY    = 'pb_first_touch_brand';

  const LAST_TOUCH_COOKIE  = 'pb_last_touch_brand';
  const LAST_TOUCH_KEY     = 'pb_last_touch_brand';

  const AFFILIATE_COOKIE = 'pb_affiliate_name';
  const AFFILIATE_KEY    = 'pb_affiliate_name';

  function getFirstTouchBrand() {
    return getCookie(FIRST_TOUCH_COOKIE) || 'planetb';
  }

  // Make this idempotent so it never becomes last_last_*
  function getLastTouchValue() {
    const raw = getCookie(LAST_TOUCH_COOKIE) || 'planetb';
    const brand = String(raw || 'planetb').trim().toLowerCase();
    return brand.startsWith('last_') ? brand : `last_${brand}`;
  }

  function getAffiliateName() {
    return getCookie(AFFILIATE_COOKIE) || null;
  }

  // ---------- ensureAttributionAttributes ----------
  // Only fires a cartAttributesUpdate when the values are actually missing or outdated.
  // This prevents a race condition (CONFLICT error) caused by two simultaneous Storefront
  // API mutations on the same cart — which happened because addLine + ensureAttributionAttributes
  // were both mutating the cart in quick succession on every add.
  async function ensureAttributionAttributes(cart) {
    if (!cart?.id) return cart;

    const first     = getFirstTouchBrand();
    const last      = getLastTouchValue();
    const affiliate = getAffiliateName();

    const existingAttrs = cart.attributes || [];

    // Check if our attribution attributes are already correctly set
    const firstOk     = existingAttrs.some(a => a.key === FIRST_TOUCH_KEY && a.value === String(first));
    const lastOk      = existingAttrs.some(a => a.key === LAST_TOUCH_KEY  && a.value === String(last));
    const affiliateOk = !affiliate || existingAttrs.some(a => a.key === AFFILIATE_KEY && a.value === affiliate);

    // Skip the API call entirely if nothing needs to change
    if (firstOk && lastOk && affiliateOk) return cart;

    // Preserve any existing attributes that aren't ours (e.g. Convert keys)
    const preserved = existingAttrs
      .filter(a => a.key !== FIRST_TOUCH_KEY && a.key !== LAST_TOUCH_KEY && a.key !== WONDR_PRIZE_ATTRIBUTE && a.key !== AFFILIATE_KEY)
      .map(a => ({ key: a.key, value: a.value }));

    const toSet = [
      ...preserved,
      { key: FIRST_TOUCH_KEY, value: String(first) },
      { key: LAST_TOUCH_KEY,  value: String(last)  },
      ...(affiliate ? [{ key: AFFILIATE_KEY, value: affiliate }] : [])
    ];

    const updated = await cartAttributesUpdate(cart.id, toSet);
    _cartPromise = Promise.resolve(updated);
    return updated;
  }


  // ---------- PUBLIC API ----------
  const PBCart = {
    async refresh() {
      await ensureCart();
      try {
          let c = await cartQuery(_cartId);
          c = await ensureAttributionAttributes(c);
          _cartPromise = Promise.resolve(c);
          document.dispatchEvent(updatedEvent(c, "refresh"));
          return c;
      } catch (e) {
          // ── NIEUW: retry één keer voordat je een nieuwe cart maakt ──
          try {
              await new Promise(r => setTimeout(r, 500));
              let c = await cartQuery(_cartId);
              c = await ensureAttributionAttributes(c);
              _cartPromise = Promise.resolve(c);
              document.dispatchEvent(updatedEvent(c, "refresh"));
              return c;
          } catch (retryErr) {
              console.warn('[PBCart] refresh failed after retry, creating new cart', retryErr);
              let newCart = await cartCreate();
              newCart = await ensureAttributionAttributes(newCart);
              _cartId = newCart.id;
              setCookie(CART_COOKIE, _cartId);
              _cartPromise = Promise.resolve(newCart);
              document.dispatchEvent(updatedEvent(newCart, "refresh:new"));
              return newCart;
          }
      }
  },

    async addLine(variantIdOrGid, qty = 1, attributes = [], sellingPlanId = null) {
      await ensureCart();

      const gid = gidFromVariant(variantIdOrGid);

      const sellingPlanGid = sellingPlanId
        ? (String(sellingPlanId).startsWith('gid://') 
            ? String(sellingPlanId) 
            : `gid://shopify/SellingPlan/${String(sellingPlanId).replace(/\D/g, '')}`)
        : null;
      
      const payload = {
        merchandiseId: gid,
        quantity: Math.max(1, Number(qty) || 1)
      };
      if (attributes?.length) payload.attributes = attributes;
      if (sellingPlanId) payload.sellingPlanId = sellingPlanGid;

      window.PBDebug?.log("=== PBCart.addLine CALLED ===", { variant: variantIdOrGid, qty });

      const c = await cartLinesAdd(_cartId, [payload]);

      // ensureAttributionAttributes will only fire a cartAttributesUpdate if the
      // attribution values are missing or outdated — avoiding the CONFLICT error
      // that occurred when two mutations fired simultaneously on the same cart.
      const cart = await ensureAttributionAttributes(c);

      // Find the line that matches the variant we added (don't assume edges[0])
      const lines = (cart?.lines?.edges || []).map(e => e.node);
      const line = lines.find(n => n?.merchandise?.id === gid) || lines[lines.length - 1];

      /*
      if (!line) {
        console.warn('[PBCart] addLine: line not found for', gid);
      } else {
        // --- Klaviyo tracking (safe wrapper) ---
        await klaviyoTrack('planetb_add_to_cart', {
          cart_id: cart?.id,
          variant_id: line?.merchandise?.id,
          product_id: line?.merchandise?.product?.id,
          product_handle: line?.merchandise?.product?.handle,
          product_name: line?.merchandise?.product?.title,
          variant_name: line?.merchandise?.title,
          sku: line?.merchandise?.sku || null,
          price: Number(line?.merchandise?.price?.amount || 0),
          currency: line?.merchandise?.price?.currencyCode,
          quantity: Number(line?.quantity || payload.quantity),
          ts: Date.now()
        });
      }
      */

      document.dispatchEvent(new CustomEvent('cart:open'));

      _cartPromise = Promise.resolve(cart);
      document.dispatchEvent(updatedEvent(cart, "add"));
      return cart;
    },

    async updateLine(lineId, qty) {
      await ensureCart();

      let cart = await cartLinesUpdate(_cartId, [{ id: lineId, quantity: qty }]);
      cart = await ensureAttributionAttributes(cart);

      _cartPromise = Promise.resolve(cart);
      document.dispatchEvent(updatedEvent(cart, "update"));
      return cart;
    },

    async replacePickMixBundle(payload = {}) {
      await ensureCart();

      window.PBDebug?.log('[PBCart] replacePickMixBundle called', payload);

      const cartItems = Array.isArray(payload.cartItems) ? payload.cartItems : [];

      if (!cartItems.length) {
        throw new Error('[PBCart] Pick & Mix payload has no cartItems');
      }

      const firstPickMixProps = cartItems[0]?.properties || {};

      const pickMixBundleId =
        firstPickMixProps._pick_mix_id ||
        firstPickMixProps.pb_pick_mix_id ||
        '';

      const pickMixEditUrl =
        payload.editUrl ||
        localStorage.getItem('pb_pick_mix_edit_url_latest') ||
        '/pages/pick-mix?pm_edit=1';

      function cleanVariantId(value) {
        const raw = String(value || '');
        const lastPart = raw.includes('/') ? raw.split('/').pop() : raw;
        return lastPart.replace(/\D/g, '') || raw;
      }

      function getPickMixImageUrl(item) {
        const img =
          item.image ||
          item.imageUrl ||
          item.featured_image ||
          item.featuredImage ||
          item.productImage ||
          item.media ||
          '';

        if (typeof img === 'string') return img;

        if (img?.url) return img.url;
        if (img?.src) return img.src;
        if (img?.image?.url) return img.image.url;
        if (img?.image?.src) return img.image.src;
        if (img?.preview_image?.src) return img.preview_image.src;
        if (img?.featured_image?.src) return img.featured_image.src;

        return '';
      }

      const displaySourceItems = Array.isArray(payload.basketItems) && payload.basketItems.length
        ? payload.basketItems
        : cartItems;

      const pickMixDisplayItems = displaySourceItems.map((item) => {
        const props = item.properties || {};
        const variantId = cleanVariantId(item.variantId || item.id);

        return {
          variantId,
          id: variantId,

          title:
            props._pick_mix_card_title ||
            props.pb_pick_mix_card_title ||
            item.title ||
            'Item',

          subtitle:
            props._pick_mix_subtitle ||
            props.pb_pick_mix_subtitle ||
            item.subtitle ||
            '',

          variantTitle:
            item.variantTitle ||
            props._pick_mix_variant_title ||
            props.pb_pick_mix_variant_title ||
            '',
          quantity: Math.max(1, Number(item.quantity) || 1),
          image: getPickMixImageUrl(item),
          url: item.url || '#',
          price: Number(item.price || 0)
        };
      });

      window.PB_PICK_MIX_DISPLAY_BY_ID = window.PB_PICK_MIX_DISPLAY_BY_ID || {};
      window.PB_LAST_PICK_MIX_DISPLAY = pickMixDisplayItems;

      if (pickMixBundleId) {
        window.PB_PICK_MIX_DISPLAY_BY_ID[pickMixBundleId] = pickMixDisplayItems;
      }

      try {
        localStorage.setItem('pb_pick_mix_display_latest', JSON.stringify(pickMixDisplayItems));
        localStorage.setItem('pb_pick_mix_edit_url_latest', pickMixEditUrl);

        if (pickMixBundleId) {
          localStorage.setItem(
            `pb_pick_mix_display_${pickMixBundleId}`,
            JSON.stringify(pickMixDisplayItems)
          );

          localStorage.setItem(
            `pb_pick_mix_edit_url_${pickMixBundleId}`,
            pickMixEditUrl
          );
        }
      } catch (e) {
        console.warn('[PBCart] Could not save Pick & Mix display data', e);
      }

      let cart = _cartPromise ? await _cartPromise : await cartQuery(_cartId);

      const existingPickMixLineIds = (cart?.lines?.edges || [])
        .map((edge) => edge.node)
        .filter((line) => {
          const attrs = line.attributes || [];
          const productTitle = String(line?.merchandise?.product?.title || '').toLowerCase();

          return (
            productTitle.includes('pick & mix') ||
            attrs.some((attr) => {
              return (
                (attr.key === '_pick_mix' || attr.key === 'pb_pick_mix') &&
                attr.value === 'true'
              );
            })
          );
        })
        .map((line) => line.id);

      if (existingPickMixLineIds.length) {
        window.PBDebug?.log('[PBCart] Removing existing Pick & Mix lines', existingPickMixLineIds);
        cart = await cartLinesRemove(_cartId, existingPickMixLineIds);
      }

      const lines = cartItems.map((item) => {
        const properties = item.properties || {};

        const attributes = Object.entries(properties)
          .filter(([key, value]) => key && value !== undefined && value !== null)
          .map(([key, value]) => {
            return {
              key,
              value: String(value)
            };
          });

        return {
          merchandiseId: gidFromVariant(item.id || item.variantId),
          quantity: Math.max(1, Number(item.quantity) || 1),
          attributes
        };
      });

      window.PBDebug?.log('[PBCart] Adding Pick & Mix lines', lines);

      cart = await cartLinesAdd(_cartId, lines);
      cart = await ensureAttributionAttributes(cart);

      _cartPromise = Promise.resolve(cart);

      document.dispatchEvent(updatedEvent(cart, 'pickMixReplace'));
      document.dispatchEvent(new CustomEvent('cart:open'));

      return cart;
    },
    async removeLine(lineId) {
      await ensureCart();

      let cart = await cartLinesRemove(_cartId, [lineId]);
      cart = await ensureAttributionAttributes(cart);

      _cartPromise = Promise.resolve(cart);
      document.dispatchEvent(updatedEvent(cart, "remove"));
      return cart;
    },
    async removeLines(lineIds = []) {
      await ensureCart();

      const cleanLineIds = [...new Set(lineIds.filter(Boolean))];

      if (!cleanLineIds.length) {
        return _cartPromise || this.refresh();
      }

      let cart = await cartLinesRemove(_cartId, cleanLineIds);
      cart = await ensureAttributionAttributes(cart);

      _cartPromise = Promise.resolve(cart);
      document.dispatchEvent(updatedEvent(cart, "remove"));

      return cart;
    },

    async setAttributes(obj = {}) {
      await ensureCart();

      const existingCart = _cartPromise ? await _cartPromise : null;
      const existingAttrs = existingCart?.attributes || [];

      // Merge new attributes with existing ones (including attribution keys)
      const newEntries = Object.entries(obj).map(([k, v]) => ({ key: k, value: String(v) }));
      const newKeys = new Set(newEntries.map(a => a.key));

      const preserved = existingAttrs
        .filter(a => !newKeys.has(a.key))
        .map(a => ({ key: a.key, value: a.value }));

      const attrs = [...preserved, ...newEntries];

      let cart = await cartAttributesUpdate(_cartId, attrs);
      // After a manual setAttributes, re-check attribution in case keys were overwritten
      cart = await ensureAttributionAttributes(cart);

      _cartPromise = Promise.resolve(cart);
      document.dispatchEvent(updatedEvent(cart, "attributes"));
      return cart;
    },

    current() {
      return _cartPromise || this.refresh();
    },

    getCartId() {
      return _cartId || getCookie(CART_COOKIE);
    },

    setCartId(id) {
      _cartId = id || null;
      if (_cartId) setCookie(CART_COOKIE, _cartId);
      else delCookie(CART_COOKIE);
      _cartPromise = _cartId ? cartQuery(_cartId) : null;
      return _cartId;
    },

    async checkoutUrl() {
      let c = await this.refresh();
      c = await ensureAttributionAttributes(c);
      return c?.checkoutUrl || "";
    },

    async clear() {
      delCookie(CART_COOKIE);
      _cartId = null;
      _cartPromise = null;
      const c = await this.refresh();
      return c;
    }
  };

  window.PBCart = PBCart;

  // ---------- AUTO HOOK ----------
  if (AUTO_HOOK()) {
    document.addEventListener(
      "submit",
      async (e) => {
        const form = e.target?.closest('form[action*="/cart/add"]');
        if (!form) return;

        const idInput = form.querySelector('input[name="id"]');
        const qtyInput = form.querySelector('input[name="quantity"]');

        const variant = idInput?.value;
        const qty = parseInt(qtyInput?.value || "1", 10);

        const sellingPlanId = form.querySelector('[data-selling-plan-id]')?.dataset?.sellingPlanId || null;

        if (!variant) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        await PBCart.addLine(variant, qty, [], sellingPlanId);
      },
      { capture: true }
    );
  }

  // ---------- INIT ----------
  (async () => {
    try {
      await ensureCart();
    } catch (e) {
      console.error("[PBCart][boot]", e);
    }
  })();

  // ---------- WONDR 5 JAAR — PRIZE DRAW ----------
  const WONDR_PRIZE_ATTRIBUTE = 'wondr_prize_number';

  const WONDR_PRIZE_PROBABILITIES = [
    { number: 1, weight: 0.125 }, // Kids bar apple splash
    { number: 2, weight: 0.125 }, // All-in-1 blossom bliss
    { number: 3, weight: 0.125 }, // Kaarshouder crème + Cosy spice refill
    { number: 4, weight: 0.125 }, // Scrub reviving mocha
    { number: 5, weight: 0.125 }, // Scalp massager wit
    { number: 6, weight: 0.125 }, // Travel case Yellow
    { number: 7, weight: 0.125 }, // Conditioner lavender haze
    { number: 8, weight: 0.125 }, // Clay detox mask
  ];

  function wondrDrawPrize(probabilities) {
    const rand = Math.random();
    let cumulative = 0;
    for (const prize of probabilities) {
      cumulative += prize.weight;
      if (rand < cumulative) return prize.number;
    }
    return null;
  }

  async function wondrMaybeDrawPrize() {
    try {
      // 🔒 Campagne guard — enkel actief in test mode of wanneer campagne live is
      const isTestMode = new URLSearchParams(location.search).get('wondr_test') === '1';
      const isCampaignLive = false; // ← zet op false op 30/03

      if (!isTestMode && !isCampaignLive) {
        window.PBDebug?.log('[Wondr] Prize draw: inactief');
        return null;
      }

      const cart = await PBCart.current();
      const lines = (cart?.lines?.edges || []).map(e => e.node);

      const eligibleQty = lines
        .filter(line => !line.attributes?.some(a => a.key === '_auto_gift' && a.value === 'true'))
        .reduce((sum, line) => sum + line.quantity, 0);

      window.PBDebug?.log('[Wondr] Eligible qty:', eligibleQty);

      if (eligibleQty < 4) {
        window.PBDebug?.log('[Wondr] Prize draw: not eligible');
        return null;
      }

      const existing = cart.attributes?.find(a => a.key === WONDR_PRIZE_ATTRIBUTE);
      if (existing?.value) {
        window.PBDebug?.log('[Wondr] Prize already drawn:', existing.value);
        return existing.value;
      }

      const prizeNumber = wondrDrawPrize(WONDR_PRIZE_PROBABILITIES);
      if (prizeNumber) {
        await PBCart.setAttributes({ [WONDR_PRIZE_ATTRIBUTE]: String(prizeNumber) });
        window.PBDebug?.log('[Wondr] Prize drawn:', prizeNumber);
        return prizeNumber;
      }
    } catch (e) {
      console.warn('[Wondr] Prize draw failed', e);
    }
    return null;
  }

  window.wondrMaybeDrawPrize = wondrMaybeDrawPrize;

})();